import type { AppCategory } from '@/data/apps'
import type { TransitionOverrides, TransitionPresetName } from '@/utils/transitions'

export type WindowAnimationContext = 'system' | 'productivity' | 'multimedia' | 'admin' | 'communication'

export type WindowAnimationPreset = {
  enter: {
    translateX?: string
    translateY?: string
    scale?: number
    opacity?: number
    blur?: string
    timingPreset: TransitionPresetName
    timingOverrides?: TransitionOverrides
  }
  exit: {
    translateX?: string
    translateY?: string
    scale?: number
    opacity?: number
    blur?: string
    timingPreset: TransitionPresetName
    timingOverrides?: TransitionOverrides
  }
}

const PRESETS: Record<WindowAnimationContext, WindowAnimationPreset> = {
  system: {
    enter: {
      translateY: '18px',
      scale: 0.95,
      opacity: 0,
      blur: '0.35px',
      timingPreset: 'standard',
      timingOverrides: { context: 'window', phase: 'enter', clamp: { min: 160, max: 320 } },
    },
    exit: {
      translateY: '16px',
      scale: 0.94,
      opacity: 0,
      blur: '0.2px',
      timingPreset: 'standard',
      timingOverrides: { context: 'window', phase: 'exit', clamp: { min: 150, max: 300 } },
    },
  },
  productivity: {
    enter: {
      translateY: '12px',
      scale: 0.98,
      opacity: 0,
      blur: '0.25px',
      timingPreset: 'quick',
      timingOverrides: { context: 'window', phase: 'enter', clamp: { min: 130, max: 240 } },
    },
    exit: {
      translateY: '10px',
      scale: 0.96,
      opacity: 0,
      blur: '0.15px',
      timingPreset: 'quick',
      timingOverrides: { context: 'window', phase: 'exit', clamp: { min: 120, max: 220 } },
    },
  },
  multimedia: {
    enter: {
      translateY: '14px',
      translateX: '8px',
      scale: 0.9,
      opacity: 0,
      blur: '0.45px',
      timingPreset: 'expressive',
      timingOverrides: { context: 'window', phase: 'enter', clamp: { min: 180, max: 360 } },
    },
    exit: {
      translateY: '18px',
      translateX: '12px',
      scale: 0.85,
      opacity: 0,
      blur: '0.35px',
      timingPreset: 'expressive',
      timingOverrides: { context: 'window', phase: 'exit', clamp: { min: 170, max: 340 } },
    },
  },
  admin: {
    enter: {
      translateX: '24px',
      translateY: '4px',
      scale: 0.93,
      opacity: 0,
      blur: '0.3px',
      timingPreset: 'standard',
      timingOverrides: { context: 'window', phase: 'enter', clamp: { min: 160, max: 320 } },
    },
    exit: {
      translateX: '28px',
      translateY: '8px',
      scale: 0.9,
      opacity: 0,
      blur: '0.25px',
      timingPreset: 'standard',
      timingOverrides: { context: 'window', phase: 'exit', clamp: { min: 150, max: 300 } },
    },
  },
  communication: {
    enter: {
      translateY: '10px',
      scale: 0.96,
      opacity: 0,
      blur: '0.2px',
      timingPreset: 'micro',
      timingOverrides: { context: 'window', phase: 'enter', clamp: { min: 120, max: 220 } },
    },
    exit: {
      translateY: '12px',
      scale: 0.94,
      opacity: 0,
      blur: '0.1px',
      timingPreset: 'micro',
      timingOverrides: { context: 'window', phase: 'exit', clamp: { min: 110, max: 200 } },
    },
  },
}

const FALLBACK_PRESET = PRESETS.system

export function getWindowAnimationPreset(context: WindowAnimationContext | undefined | null): WindowAnimationPreset {
  if (!context) return FALLBACK_PRESET
  return PRESETS[context] ?? FALLBACK_PRESET
}

export function resolveWindowAnimationContext(appId: string, category?: AppCategory): WindowAnimationContext {
  if (appId === 'communicator2') return 'communication'

  switch (category) {
    case 'admin':
      return 'admin'
    case 'multimedia':
      return 'multimedia'
    case 'productivity':
      return 'productivity'
    case 'system':
    default:
      return 'system'
  }
}
